import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AccountSwitcher,
  AvatarDropdown,
  Footer,
  SelectLang,
} from '@/components';
import { TokenManager, getUserAccountList, type UserAccount } from '@/services/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/** 默认头像 */
const DEFAULT_AVATAR = 'https://gw.alipayobjects.com/zos/rmsportal/BiazfanxmamNRoxxVxka.png';

/** 用户信息类型 */
interface CurrentUser {
  /** 用户ID */
  loginId?: number;
  /** 用户名 */
  name?: string;
  /** 头像 */
  avatar?: string;
  /** 当前账号ID */
  accountId?: number;
  /** 当前账号名称 */
  accountName?: string;
  /** 账号列表 */
  accounts?: UserAccount[];
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (): Promise<CurrentUser | undefined> => {
    // 检查是否有 token
    if (!TokenManager.isLoggedIn()) {
      return undefined;
    }

    try {
      // 调用接口获取用户账号列表
      const result = await getUserAccountList();
      const accounts = result.accounts || [];
      // 获取选中的账号或第一个账号
      const selectedAccount = accounts.find((a) => a.isSelect) || accounts[0];

      // 保存当前账号ID
      if (selectedAccount?.accountId) {
        TokenManager.setAccountId(selectedAccount.accountId);
      }

      return {
        loginId: result.loginId,
        name: result.name || `用户 ${result.loginId}`,
        avatar: result.avatar,
        accountId: selectedAccount?.accountId,
        accountName: selectedAccount?.accountName,
        accounts,
      };
    } catch (error) {
      // 获取用户信息失败，清除 token
      console.error('获取用户信息失败:', error);
      TokenManager.clearAll();
      return undefined;
    }
  };

  // 如果不是登录页面，检查登录状态
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    // 未登录则跳转到登录页
    if (!currentUser) {
      history.push(loginPath);
      return {
        fetchUserInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    // 侧边菜单宽度
    siderWidth: 156,
    // 左上角：账号切换器替代 Logo
    headerTitleRender: () => <AccountSwitcher />,
    // 隐藏默认 Logo
    logo: false,
    // 阻止点击 Logo 区域跳转首页
    onMenuHeaderClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    // 右侧操作区（移除帮助图标）
    actionsRender: () => [
      <SelectLang key="SelectLang" />,
    ],
    // 隐藏 PageContainer 的面包屑导航
    breadcrumbRender: false,
    // 隐藏 PageContainer 的页面标题
    pageTitleRender: false,
    // 右侧头像（通过配置显示头像和用户名）
    avatarProps: {
      src: initialState?.currentUser?.avatar || DEFAULT_AVATAR,
      title: initialState?.currentUser?.name,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: 'https://api.liteinn.com',
  ...errorConfig,
};
